import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, ChevronLeft, Minus, Plus, Heart, Share2, Truck, RotateCcw, Shield, Loader2, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { trackEvent, trackProductView } from '../hooks/useAnalytics';
import ProductReviews from '../components/ProductReviews';
import { getImageUrl } from '../lib/utils';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allProducts, setAllProducts] = useState([]);
  const { addToCart, setIsCartOpen } = useCart();
  const { isAuthenticated, login } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  // Fetch product from API
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const [productRes, allProductsRes] = await Promise.all([
          fetch(`${backendUrl}/api/products/${id}`),
          fetch(`${backendUrl}/api/products`)
        ]);
        
        if (productRes.ok) {
          const data = await productRes.json();
          setProduct(data);
          // Pick the first size that still has stock; fall back to first listed size
          const sizes = data.sizes || [];
          const inStockSize = sizes.find((s) => (data.stock?.[s] ?? 0) > 0);
          setSelectedSize(inStockSize || sizes[0] || '65x65');
          setSelectedColor(data.colors?.[0] || '');
        }
        
        if (allProductsRes.ok) {
          setAllProducts(await allProductsRes.json());
        }
      } catch (error) {
        console.error('Product fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, backendUrl]);

  // Scroll to top when product changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Track time spent on product
  const startTime = useRef(Date.now());
  const maxScrollDepth = useRef(0);

  useEffect(() => {
    if (!product) return;
    
    // Track product view event
    trackEvent({
      event_type: 'product_view',
      product_id: product.id,
      page: `/urun/${product.id}`,
      metadata: { product_name: product.name }
    });
    
    // Reset timer when product changes
    startTime.current = Date.now();
    maxScrollDepth.current = 0;
    
    // Track scroll depth
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      maxScrollDepth.current = Math.max(maxScrollDepth.current, scrollPercent);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    // Track time spent when leaving the page
    return () => {
      window.removeEventListener('scroll', handleScroll);
      const duration = Date.now() - startTime.current;
      
      // Only track if spent more than 1 second
      if (duration > 1000) {
        trackProductView(product.id, duration, maxScrollDepth.current);
      }
    };
  }, [product?.id]);

  const productIsFavorite = product ? isFavorite(product.id) : false;

  const handleFavoriteClick = async () => {
    if (!isAuthenticated) {
      login();
      return;
    }

    setFavoriteLoading(true);
    await toggleFavorite(product.id);
    setFavoriteLoading(false);
  };

  // Calculate current price based on selected size
  const currentPrice = useMemo(() => {
    if (product?.prices && product.prices[selectedSize]) {
      return product.prices[selectedSize];
    }
    return product?.price || 0;
  }, [product, selectedSize]);

  // Calculate display name based on selected size (for products with altName)
  // Show altName for any size other than 65x65
  const displayName = useMemo(() => {
    if (product?.altName && selectedSize !== '65x65') {
      return product.altName;
    }
    return product?.name || '';
  }, [product, selectedSize]);

  // Get images based on selected size
  // If size-specific images exist, show those; otherwise show all images
  const displayImages = useMemo(() => {
    if (product?.sizeImages && product.sizeImages[selectedSize] && product.sizeImages[selectedSize].length > 0) {
      return product.sizeImages[selectedSize];
    }
    return product?.images || [];
  }, [product, selectedSize]);

  // Reset image index when size changes and size has specific images
  useEffect(() => {
    if (product?.sizeImages && product.sizeImages[selectedSize]) {
      setActiveImageIndex(0);
    }
  }, [selectedSize, product?.sizeImages]);

  // Slider navigation functions
  const nextImage = () => {
    if (displayImages?.length > 1) {
      setActiveImageIndex((prev) => (prev + 1) % displayImages.length);
    }
  };

  const prevImage = () => {
    if (displayImages?.length > 1) {
      setActiveImageIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
    }
  };

  // Stock helpers (per size, total) - must be defined before any early return
  const sizeStockMap = product?.stock || {};
  const currentStock = Math.max(0, parseInt(sizeStockMap[selectedSize] ?? 0, 10) || 0);
  const totalStock = Object.values(sizeStockMap).reduce(
    (acc, v) => acc + (parseInt(v, 10) || 0),
    0,
  );
  const isOutOfStock = totalStock === 0;
  const isSelectedOutOfStock = currentStock === 0;

  // Clamp quantity to current stock
  useEffect(() => {
    if (currentStock > 0 && quantity > currentStock) {
      setQuantity(currentStock);
    }
    if (currentStock === 0 && quantity !== 1) {
      setQuantity(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStock]);

  if (loading) {
    return (
      <div className="pt-32 pb-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-32 pb-20 text-center">
        <h1 className="text-2xl font-light">Ürün bulunamadı</h1>
        <Link to="/urunler" className="text-sm underline mt-4 inline-block">
          Ürünlere dön
        </Link>
      </div>
    );
  }

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleAddToCart = () => {
    if (isSelectedOutOfStock) return;
    const cartProduct = {
      ...product,
      name: displayName,
      price: currentPrice,
      selectedSize,
      selectedColor,
      dimensions: `${selectedSize} cm`,
    };
    addToCart(cartProduct, selectedColor, Math.min(quantity, currentStock));
    setIsCartOpen(true);
  };

  const relatedProducts = allProducts
    .filter((p) => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  return (
    <div className="pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-black">Ana Sayfa</Link>
          <ChevronRight size={14} />
          <Link to="/urunler" className="hover:text-black">Ürünler</Link>
          <ChevronRight size={14} />
          <span className="text-black">{displayName}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Images with Slider */}
          <div className="space-y-4">
            <div className="relative aspect-square bg-white overflow-hidden group border">
              <img
                src={getImageUrl(displayImages[activeImageIndex] || product.image)}
                alt={displayName}
                className="w-full h-full object-contain transition-opacity duration-300"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/600x600?text=VALMÉRA';
                }}
              />
              
              {/* Slider Navigation Arrows */}
              {displayImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    aria-label="Önceki görsel"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    aria-label="Sonraki görsel"
                  >
                    <ChevronRight size={24} />
                  </button>
                  
                  {/* Dots Indicator */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {displayImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setActiveImageIndex(index)}
                        className={`w-2.5 h-2.5 rounded-full transition-all ${
                          activeImageIndex === index 
                            ? 'bg-black w-6' 
                            : 'bg-black/30 hover:bg-black/50'
                        }`}
                        aria-label={`Görsel ${index + 1}`}
                      />
                    ))}
                  </div>
                  
                  {/* Image Counter */}
                  <div className="absolute top-4 right-4 bg-black/70 text-white text-xs px-3 py-1 rounded-full">
                    {activeImageIndex + 1} / {displayImages.length}
                  </div>
                </>
              )}
            </div>
            
            {/* Thumbnail Gallery */}
            {displayImages.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {displayImages.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 border-2 overflow-hidden transition-all ${
                      activeImageIndex === index 
                        ? 'border-black ring-2 ring-black ring-offset-2' 
                        : 'border-gray-200 hover:border-gray-400'
                    }`}
                  >
                    <img 
                      src={getImageUrl(img)} 
                      alt="" 
                      className="w-full h-full object-contain bg-white"
                      onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/80'; }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            {product.isNew && (
              <span className="inline-block bg-black text-white text-xs px-3 py-1 tracking-wider mb-4">
                YENİ
              </span>
            )}
            <h1 className="text-3xl font-light tracking-wider mb-4">{displayName}</h1>
            
            <div className="flex items-center gap-4 mb-6">
              <span className="text-2xl">
                {formatPrice(currentPrice)}
              </span>
            </div>

            <p className="text-gray-600 leading-relaxed mb-8">{product.description}</p>

            {/* Size Selection */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-medium tracking-wider mb-4">
                  BOYUT: <span className="font-normal">{selectedSize} cm</span>
                </h3>
                <div className="flex gap-3 flex-wrap">
                  {product.sizes.map((size) => {
                    const stock = parseInt(sizeStockMap[size] ?? 0, 10) || 0;
                    const sizeOOS = stock === 0;
                    const selected = selectedSize === size;
                    return (
                      <button
                        key={size}
                        data-testid={`size-btn-${size}`}
                        onClick={() => !sizeOOS && setSelectedSize(size)}
                        disabled={sizeOOS}
                        className={`min-w-[120px] px-4 py-3 border text-sm transition-all relative ${
                          sizeOOS
                            ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed line-through'
                            : selected
                            ? 'border-black bg-black text-white'
                            : 'border-gray-300 hover:border-black bg-white'
                        }`}
                      >
                        <div className="font-medium">{size} cm</div>
                        <div className={`text-xs mt-1 ${selected && !sizeOOS ? 'text-gray-300' : 'text-gray-500'}`}>
                          {formatPrice(product.prices?.[size] || product.price)}
                        </div>
                        <div className={`text-[10px] mt-1 ${
                          sizeOOS ? 'text-red-500 font-semibold'
                          : stock <= 2 ? 'text-amber-600 font-medium'
                          : selected ? 'text-gray-300'
                          : 'text-gray-500'
                        }`}>
                          {sizeOOS ? 'TÜKENDİ' : stock <= 2 ? `Son ${stock} adet` : `${stock} adet stokta`}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {product.colors && product.colors.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-medium tracking-wider mb-4">
                  RENK: <span className="font-normal">{selectedColor}</span>
                </h3>
                <div className="flex flex-wrap gap-3">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`px-4 py-2 border text-sm transition-colors ${
                        selectedColor === color
                          ? 'border-black bg-black text-white'
                          : 'border-gray-300 hover:border-black'
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Product Features - Özel Tasarım, El Dikişi, Cilt Dostu */}
            {product.features && product.features.length > 0 && (
              <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex flex-wrap gap-4">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-amber-600 rounded-full"></span>
                      <span className="text-sm font-medium text-amber-900">{feature}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-amber-700 mt-3">
                  Has ipek, cildi nemlendirmeye yardımcı olur, saç kırılmasını önler ve doğal hipoalerjenik yapısıyla hassas ciltler için idealdir.
                </p>
              </div>
            )}

            {/* Quantity */}
            <div className="mb-8">
              <h3 className="text-sm font-medium tracking-wider mb-4">
                ADET
                {!isSelectedOutOfStock && currentStock > 0 && (
                  <span className="text-xs font-normal text-gray-500 ml-2">
                    (en fazla {currentStock} adet)
                  </span>
                )}
              </h3>
              <div className={`flex items-center border w-fit ${isSelectedOutOfStock ? 'border-gray-200 opacity-50' : 'border-gray-300'}`}>
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={isSelectedOutOfStock || quantity <= 1}
                  className="p-4 hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <Minus size={16} />
                </button>
                <span className="px-6 text-center min-w-[60px]">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(currentStock || 1, quantity + 1))}
                  disabled={isSelectedOutOfStock || quantity >= currentStock}
                  className="p-4 hover:bg-gray-50 transition-colors disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4 mb-8">
              <button
                data-testid="buy-on-shopier-btn"
                onClick={() => !isOutOfStock && window.open('https://www.shopier.com/valmeraofficial', '_blank', 'noopener,noreferrer')}
                disabled={isOutOfStock}
                className={`flex-1 py-4 text-sm tracking-wider transition-colors ${
                  isOutOfStock
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-black text-white hover:bg-gray-900'
                }`}
              >
                {isOutOfStock ? 'TÜKENDİ' : 'SATIN AL'}
              </button>
              <button
                data-testid="add-to-cart-btn"
                onClick={handleAddToCart}
                disabled={isSelectedOutOfStock}
                className={`p-4 border transition-colors ${
                  isSelectedOutOfStock
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'border-gray-300 hover:border-black'
                }`}
                title={isSelectedOutOfStock ? 'Bu boyut tükendi' : 'Sepete ekle'}
              >
                <ShoppingBag size={20} />
              </button>
              <button 
                onClick={handleFavoriteClick}
                disabled={favoriteLoading}
                className={`p-4 border transition-colors ${
                  productIsFavorite 
                    ? 'border-red-500 bg-red-50 text-red-500' 
                    : 'border-gray-300 hover:border-black'
                }`}
                title={productIsFavorite ? 'Favorilerden kaldır' : 'Favorilere ekle'}
              >
                <Heart size={20} fill={productIsFavorite ? 'currentColor' : 'none'} />
              </button>
              <button className="p-4 border border-gray-300 hover:border-black transition-colors">
                <Share2 size={20} />
              </button>
            </div>

            {/* Product Details */}
            <div className="border-t pt-8 space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <span className="text-gray-500 w-24">Malzeme:</span>
                <span>{product.material}</span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-gray-500 w-24">Boyutlar:</span>
                <span>{selectedSize} cm</span>
              </div>
            </div>

            {/* Shipping Info */}
            <div className="mt-8 pt-8 border-t space-y-4">
              <div className="flex items-center gap-4 text-sm">
                <Truck size={20} className="text-gray-400" />
                <span>Ücretsiz kargo | 500₺ üzeri siparişlerde</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <RotateCcw size={20} className="text-gray-400" />
                <span>14 gün kolay iade</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <Shield size={20} className="text-gray-400" />
                <span>Orijinal ürün garantisi</span>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products - Products with similar style */}
        {product.relatedProducts && product.relatedProducts.length > 0 && (
          <section className="mt-20 pt-12 border-t">
            <h2 className="text-2xl font-light tracking-wider mb-8">BENZER ÜRÜNLER</h2>
            <p className="text-gray-500 mb-8">Bu ürünü beğendiyseniz, bunları da seveceksiniz</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {product.relatedProducts.map((relatedId) => {
                const relatedProduct = allProducts.find((p) => p.id === relatedId);
                if (!relatedProduct) return null;
                return (
                  <Link key={relatedProduct.id} to={`/urun/${relatedProduct.id}`} className="group">
                    <div className="relative aspect-square overflow-hidden bg-gray-100 mb-4">
                      <img
                        src={relatedProduct.image}
                        alt={relatedProduct.name}
                        className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                      />
                      {relatedProduct.isNew && (
                        <span className="absolute top-3 left-3 bg-black text-white text-xs px-2 py-1 tracking-wider">
                          YENİ
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-medium mb-2 group-hover:underline">
                      {relatedProduct.name}
                    </h3>
                    <span className="text-sm">{formatPrice(relatedProduct.prices?.['65x65'] || relatedProduct.price)}</span>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Complementary Products - Products that go well together */}
        {product.complementaryProducts && product.complementaryProducts.length > 0 && (
          <section className="mt-16 pt-12 border-t">
            <h2 className="text-2xl font-light tracking-wider mb-8">BİRLİKTE GÜZELLEŞİR</h2>
            <p className="text-gray-500 mb-8">Bu ürünle uyumlu parçalar</p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-8">
              {product.complementaryProducts.map((compId) => {
                const compProduct = allProducts.find((p) => p.id === compId);
                if (!compProduct) return null;
                return (
                  <Link key={compProduct.id} to={`/urun/${compProduct.id}`} className="group flex gap-4 items-center p-4 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="w-24 h-24 flex-shrink-0 overflow-hidden">
                      <img
                        src={compProduct.image}
                        alt={compProduct.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium mb-1 group-hover:underline">
                        {compProduct.name}
                      </h3>
                      <p className="text-xs text-gray-500 mb-2">Has İpek Fular</p>
                      <span className="text-sm font-medium">{formatPrice(compProduct.prices?.['65x65'] || compProduct.price)}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Product Reviews */}
        <ProductReviews productId={product.id} />
      </div>
    </div>
  );
};

export default ProductDetailPage;
